<template>
  <HeadlessCombobox v-model="modelValue" as="div" class="relative">
    <div class="flex justify-end">
      <HeadlessComboboxButton
        ref="listboxButtonRef"
        :class="[
          'text-background-fg/60 flex w-full items-center text-base',
          underlined || 'py-2',
          // In the mobile sheet the button stands alone with nothing around it
          // to read as a control, so it gets an underline and a left-aligned
          // label — a field. In the header bar it sits in a row of text
          // controls and keeps that shape.
          underlined
            ? 'border-background-fg/10 hover:bg-background-fg/5 hover:text-background-fg/80 justify-between border-b px-4 py-3 transition-colors'
            : 'justify-end rounded-full px-3 text-end',
        ]"
      >
        <span class="sr-only">Change site</span>
        <span :class="['line-clamp-1', underlined && 'text-left']">
          {{ currentSite?.name || "All Sites" }}</span
        >
        <IconHeroiconsSolidChevronDown
          class="ml-1 h-6 w-6"
          aria-hidden="true"
        />
      </HeadlessComboboxButton>
    </div>

    <Teleport to="body">
      <Transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        @after-leave="query = ''"
      >
        <HeadlessComboboxOptions
          ref="listboxOptionsRef"
          class="divide-background-fg/8 bg-background absolute right-0 z-10 max-h-[60vh] w-80 origin-top-right divide-y overflow-hidden overflow-y-auto rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          as="ul"
        >
          <li class="bg-background sticky top-0 z-10 p-2">
            <div class="relative">
              <div
                class="pointer-events-none absolute inset-y-0 left-2 flex items-center"
              >
                <IconHeroiconsSolidMagnifyingGlass
                  class="text-background-fg/40 h-5 w-5"
                  aria-hidden="true"
                />
              </div>
              <HeadlessComboboxInput
                class="border-background-fg/20 text-background-fg/87 w-full rounded-md border bg-transparent py-2 pl-8 pr-3 text-sm focus:outline-none"
                :display-value="() => query"
                placeholder="Type to filter…"
                autocomplete="off"
                @change="query = ($event.target as HTMLInputElement).value"
              />
            </div>
          </li>

          <HeadlessComboboxOption
            v-for="option in filteredSiteOptions"
            :key="option.id ?? undefined"
            v-slot="{ active, selected }"
            as="template"
            :value="option.id"
          >
            <li
              :class="[
                active ? 'bg-primary text-white' : 'text-background-fg/87',
                'relative cursor-pointer select-none p-4 text-sm',
              ]"
            >
              <div class="flex flex-col">
                <div class="flex justify-between">
                  <p :class="selected ? 'font-semibold' : 'font-normal'">
                    {{ option.name }}
                  </p>
                  <span
                    v-if="selected"
                    :class="active ? 'text-white' : 'text-indigo-500'"
                  >
                    <IconHeroiconsSolidCheck
                      class="h-5 w-5"
                      aria-hidden="true"
                    />
                  </span>
                </div>
              </div>
            </li>
          </HeadlessComboboxOption>

          <li
            v-if="filteredSiteOptions.length === 0"
            class="text-background-fg/60 p-4 text-sm"
          >
            No sites match “{{ query }}”.
          </li>
        </HeadlessComboboxOptions>
      </Transition>
    </Teleport>
  </HeadlessCombobox>
</template>

<script setup lang="ts">
import { createPopper } from "@popperjs/core";
import { type ComponentPublicInstance } from "vue";
// The mobile sheet wants the selector to read as a full-bleed row whose
// underline doubles as the divider between it and the section below.
withDefaults(defineProps<{ underlined?: boolean }>(), { underlined: false });

const { id: modelValue } = useCurrentSite();
const { currentUser } = useCurrentUser();

const currentSite = computed(() =>
  siteOptions.value?.find(({ id }) => id === modelValue.value),
);
const { data: sites } = useSites();

const siteOptions = computed(() =>
  sites.value && currentUser.value
    ? [
        ...(currentUser.value.siteId
          ? []
          : [
              {
                id: null,
                name: "All Sites",
              },
            ]),
        ...sites.value,
      ]
    : [],
);

// Type-to-filter over the (already per-user-scoped) site list. The full list can
// be long now every PitchUp feed site is onboarded, so all-sites users narrow it
// by name. Filtering only affects what's rendered — the selection-validity watch
// below still runs against the full `siteOptions`.
const query = ref("");
const filteredSiteOptions = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return siteOptions.value;
  return siteOptions.value.filter((option) =>
    option.name.toLowerCase().includes(q),
  );
});

watch(
  () => siteOptions.value,
  (newOptions) => {
    // Options aren't loaded yet (sites/user still fetching). Don't touch the
    // selection or we'd clobber the persisted site restored from storage.
    if (newOptions.length === 0) return;

    const currentValueIsInvalid = !newOptions.some(
      (option) => option.id === modelValue.value,
    );

    if (currentValueIsInvalid) {
      modelValue.value = newOptions[0].id;
    }
  },
  { deep: true, immediate: true },
);

const listboxOptionsRef = ref<ComponentPublicInstance | null>(null);
const listboxButtonRef = ref<ComponentPublicInstance | null>(null);

whenever(
  () => listboxButtonRef.value?.$el && listboxOptionsRef.value?.$el,
  () => {
    createPopper(listboxButtonRef.value?.$el, listboxOptionsRef.value?.$el, {
      // Right-anchor a fixed-width panel under the (right-aligned) header
      // button, rather than matching the button's narrow width — otherwise
      // long site names wrap to three lines.
      placement: "bottom-end",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 4],
          },
        },
      ],
    });
  },
);
</script>
